import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, Checkbox, FormControlLabel, TextField } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { CrmDictType, CrmDictionaryItem, crmApi } from '../../../features/crm/crmApi'
import { Card, CardHead, Hint, Muted, PageHead, PrimaryButton, TONES, contentSx, pageSx } from '../shared/crmUi'
import { digits, money } from '../shared/format'
import { useCrmDict } from '../shared/dict'

const TYPES: CrmDictType[] = ['PACKAGE', 'PAY_TYPE', 'PAY_METHOD', 'SOURCE']

// Анықтамалықтар: что выбирают в форме продажника. Код стабилен - переименование меняет подпись и у старых записей.
const RopRefsPage = () => {
    const { formatMessage } = useIntl()
    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead title={formatMessage({ id: 'crmRefs' })} sub={formatMessage({ id: 'crmRefsSub' })} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                    {TYPES.map((t) => (
                        <RefBlock key={t} type={t} />
                    ))}
                </Box>
                <Hint>{formatMessage({ id: 'crmRefsFoot' })}</Hint>
            </Box>
        </Box>
    )
}

const linkBtn = (color: string) => ({
    border: 'none',
    background: 'transparent',
    color,
    fontWeight: 600,
    fontSize: '12.5px',
    cursor: 'pointer',
    padding: '4px 2px',
    font: 'inherit',
    whiteSpace: 'nowrap' as const,
})

const RefBlock: React.FC<{ type: CrmDictType }> = ({ type }) => {
    const { formatMessage } = useIntl()
    const dict = useCrmDict()
    const [save, { isLoading: saving }] = crmApi.useSaveDictionaryItemMutation()
    const [remove] = crmApi.useDeleteDictionaryItemMutation()
    const [editing, setEditing] = useState<string | null>(null)
    const [kz, setKz] = useState('')
    const [ru, setRu] = useState('')
    const [price, setPrice] = useState('')
    const [inst, setInst] = useState(false)
    const [dupe, setDupe] = useState(false)
    const items = dict.of(type)
    const pkg = type === 'PACKAGE'
    const payType = type === 'PAY_TYPE'

    const exists = (title: string, except?: string | null) =>
        items.some((i) => i.code !== except && [i.titleKz, i.titleRu].some((v) => (v || '').trim().toLowerCase() === title.toLowerCase()))

    const startEdit = (i: CrmDictionaryItem) => {
        setEditing(i.code)
        setKz(i.titleKz || '')
        setRu(i.titleRu || '')
        setPrice(i.price ? String(i.price) : '')
        setInst(!!i.installment)
    }

    const run = async (item: CrmDictionaryItem) => {
        try {
            await save(item).unwrap()
            return true
        } catch {
            return false
        }
    }

    const saveEdit = async (i: CrmDictionaryItem) => {
        const t = kz.trim()
        if (!t) return setEditing(null)
        if (await run({ ...i, titleKz: t, titleRu: ru.trim() || t, price: pkg ? Number(price) || 0 : i.price, installment: payType ? inst : i.installment }))
            setEditing(null)
    }

    const add = async (e: React.FormEvent) => {
        e.preventDefault()
        const t = kz.trim()
        if (!t || editing) return
        if (exists(t)) {
            setDupe(true)
            return
        }
        const ok = await run({
            type,
            code: '',
            titleKz: t,
            titleRu: ru.trim() || t,
            price: pkg ? Number(price) || 0 : 0,
            active: true,
            installment: payType && inst,
            used: 0,
            sortOrder: items.length + 1,
        })
        if (ok) {
            setKz('')
            setRu('')
            setPrice('')
            setInst(false)
        }
    }

    const fields = (
        <>
            <TextField size="small" value={kz} onChange={(e) => {
                    setKz(e.target.value)
                    setDupe(false)
                }} placeholder={formatMessage({ id: pkg ? 'crmRefPkgName' : 'crmRefNewValue' })} inputProps={{ 'aria-label': formatMessage({ id: 'crmRefTitleKz' }) }} sx={{ flex: '1 1 140px' }} />
            <TextField size="small" value={ru} onChange={(e) => setRu(e.target.value)} placeholder={formatMessage({ id: 'crmRefTitleRu' })} inputProps={{ 'aria-label': formatMessage({ id: 'crmRefTitleRu' }) }} sx={{ flex: '1 1 120px' }} />
            {pkg && (
                <TextField size="small" value={price} onChange={(e) => setPrice(digits(e.target.value))} placeholder={formatMessage({ id: 'crmRefPrice' })} inputProps={{ inputMode: 'numeric', 'aria-label': formatMessage({ id: 'crmRefPrice' }) }} sx={{ width: 120 }} />
            )}
            {payType && (
                <FormControlLabel
                    sx={{ margin: 0, '& .MuiFormControlLabel-label': { fontSize: '12.5px' } }}
                    control={<Checkbox size="small" checked={inst} onChange={(e) => setInst(e.target.checked)} />}
                    label={formatMessage({ id: 'crmRefInstallment' })}
                />
            )}
        </>
    )

    return (
        <Card sx={{ marginBottom: 0 }}>
            <CardHead title={formatMessage({ id: `crmRefTitle_${type}` })} />
            <Hint sx={{ marginTop: '-6px', marginBottom: '8px' }}>{formatMessage({ id: `crmRefDesc_${type}` })}</Hint>
            {items.map((i) => {
                const used = i.used || 0
                const on = editing === i.code
                return (
                    <Box
                        key={i.code}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap',
                            padding: '8px 0',
                            borderBottom: `1px solid ${ui.border}`,
                            opacity: i.active || on ? 1 : 0.55,
                        }}
                    >
                        {on ? (
                            <>
                                {fields}
                                <PrimaryButton size="small" disabled={saving} onClick={() => saveEdit(i)}>
                                    {formatMessage({ id: 'crmSave' })}
                                </PrimaryButton>
                                <Box component="button" type="button" onClick={() => setEditing(null)} sx={linkBtn(ui.muted)}>
                                    {formatMessage({ id: 'cancel' })}
                                </Box>
                            </>
                        ) : (
                            <>
                                <Box sx={{ flex: '1 1 140px', minWidth: 0, fontWeight: 600, fontSize: '13.5px', overflowWrap: 'anywhere' }}>
                                    {dict.title(i)}
                                    {!i.active && (
                                        <Box component="span" sx={{ color: ui.muted, fontWeight: 400, fontSize: '12px' }}>
                                            {' '}· {formatMessage({ id: 'crmRefOff' })}
                                        </Box>
                                    )}
                                    {pkg && !!i.price && <Muted>{money(i.price)}</Muted>}
                                    {payType && i.installment && <Muted>{formatMessage({ id: 'crmRefInstallment' })}</Muted>}
                                </Box>
                                <Muted sx={{ whiteSpace: 'nowrap' }}>{formatMessage({ id: 'crmStudentsN' }, { n: used })}</Muted>
                                <Box component="button" type="button" onClick={() => startEdit(i)} sx={linkBtn(ui.pink)}>
                                    {formatMessage({ id: 'crmEdit' })}
                                </Box>
                                <Box component="button" type="button" onClick={() => run({ ...i, active: !i.active })} sx={linkBtn(ui.sub)}>
                                    {formatMessage({ id: i.active ? 'crmRefTurnOff' : 'crmRefTurnOn' })}
                                </Box>
                                {!used && (
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => i.code && remove({ type, code: i.code }).unwrap().catch(() => undefined)}
                                        sx={linkBtn(TONES.bad.text)}
                                    >
                                        {formatMessage({ id: 'crmRefDelete' })}
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                )
            })}
            {!editing && (
                <Box component="form" onSubmit={add} sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '10px' }}>
                    {fields}
                    <PrimaryButton size="small" type="submit" disabled={saving || !kz.trim()}>
                        {formatMessage({ id: 'crmRefAdd' })}
                    </PrimaryButton>
                    {dupe && <Box sx={{ width: '100%', fontSize: '12px', color: TONES.bad.text }}>{formatMessage({ id: 'crmRefExists' })}</Box>}
                </Box>
            )}
        </Card>
    )
}

export default RopRefsPage
